import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, BookOpen, Home as HomeIcon,
  Heart, Compass, Sparkles, Lock, Search, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadMagnetModal from "@/components/LeadMagnetModal";
import { useSiteSettings, mediaUrl, pickText } from "@/lib/siteSettings";

const DEFAULT_BANNER = "/tammy-banner.jpg";

const HERO_IMG =
  "https://images.unsplash.com/photo-1557682250-33bd709cbe85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const pillars = [
  {
    icon: Heart,
    title: "Coaching",
    blurb: "1:1 and group coaching to help you become the person you want to be.",
    to: "/coaching",
    cta: "Explore coaching",
  },
  {
    icon: Compass,
    title: "Lifestyles",
    blurb: "Family, rhythms, and the quiet daily work of building a life you love.",
    to: "/lifestyles",
    cta: "Read more",
  },
  {
    icon: HomeIcon,
    title: "Real Estate",
    blurb: "Helping clients buy, sell, and build wealth through grounded, honest representation.",
    to: "/real-estate",
    cta: "See the work",
  },
  {
    icon: BookOpen,
    title: "The Book",
    blurb: "\"Rewriting the Family Story\" — Tammy's guide to breaking generational cycles.",
    to: "/book",
    cta: "About the book",
  },
  {
    icon: Lock,
    title: "Current Client Resources",
    blurb: "Private portal for active coaching clients — replays, templates, and tools.",
    to: "/coaching-resources",
    cta: "Open the portal",
  },
];

export default function Home() {
  const [magnetOpen, setMagnetOpen] = useState(false);
  const { settings } = useSiteSettings();
  const banner = mediaUrl(settings?.images?.homeBannerKey, DEFAULT_BANNER);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Warm light through trees" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 w-full grid md:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.32em] text-white/85 mb-6"
              data-testid="hero-eyebrow"
            >
              Tammy Richard — Real Estate Professional & Coach · Clarity | Strategy | Results
            </span>
            <h1
              className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
              data-testid="hero-title"
            >
              A grounded path to{" "}
              a <span className="italic text-[#93C5FD]" style={{ textShadow: "0 2px 24px rgba(147,197,253,0.35)" }}>thriving life.</span>
            </h1>
            <p
              className="text-white/85 text-lg md:text-xl mt-8 max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              Coaching, conversation, and resources to help you rewrite your
              story — at home, in your work, and in the life you're building.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/booking" data-testid="hero-book-btn">
                <Button
                  size="lg"
                  className="rounded-full bg-white text-primary hover:bg-white/90 btn-lift px-8 h-12 text-base"
                >
                  Book a Discovery Call <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setMagnetOpen(true)}
                data-testid="hero-magnet-btn"
                className="rounded-full bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white backdrop-blur-md h-12 px-8 text-base"
              >
                <ArrowRight className="mr-2" size={16} /> New Agent Success Playbook
              </Button>
            </div>
          </motion.div>

          {/* Search homes CTA — replaces the Portland photo card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="hidden md:block md:col-span-5"
            data-testid="hero-search-card"
          >
            <div className="relative group">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#93C5FD]/25 to-[#1E3A8A]/50 rounded-3xl blur-xl transition-opacity group-hover:opacity-100 opacity-90" />
              <a
                href={pickText(settings, "links.kwMlsUrl", "https://tammyrichard.kw.com/")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-search-link"
                className="relative block w-full h-[460px] rounded-2xl overflow-hidden border border-white/15 shadow-2xl p-8 flex flex-col justify-between transition-transform group-hover:-translate-y-1"
                style={{
                  backgroundImage: "url('/portland-lighthouse.jpg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {/* Dark gradient overlay so headline text stays legible */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F1E33]/80 via-[#1E3A8A]/65 to-[#0F1E33]/85 pointer-events-none" />
                {/* Soft radial accent */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(1200px 300px at 100% 0%, rgba(147,197,253,0.18), transparent 60%)",
                  }}
                />
                <div className="relative flex items-start justify-between">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#93C5FD]">
                    Search Maine Real Estate
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/70 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
                    kw.com <ExternalLink size={11} />
                  </span>
                </div>

                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-[#93C5FD]/15 border border-[#93C5FD]/30 flex items-center justify-center mb-6 backdrop-blur transition-transform group-hover:scale-110">
                    <Search className="text-[#93C5FD]" size={34} strokeWidth={1.7} />
                  </div>
                  <p className="font-serif text-4xl md:text-[42px] leading-[1.05] text-white">
                    Search homes
                    <br />
                    <span className="italic text-[#93C5FD]">across Maine.</span>
                  </p>
                  <p className="mt-5 text-white/80 text-sm leading-relaxed max-w-sm">
                    Every active listing on the MLS — Portland, the midcoast, the
                    lakes, and everything in between. Opens in a new tab.
                  </p>
                </div>

                <div className="relative flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-white font-medium text-base bg-white/10 border border-white/25 backdrop-blur rounded-full px-5 py-2.5 group-hover:bg-white/20 transition-colors">
                    Start searching <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">
                    Live listings
                  </span>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-intro-section">
        <img
          src={banner}
          alt="Tammy Richard — Author, Coach, Real Estate Agent"
          className="w-full max-w-[900px] mx-auto block rounded-2xl shadow-sm mb-14 md:mb-20"
          data-testid="tammy-brand-banner"
          loading="lazy"
        />
        <div className="grid md:grid-cols-12 gap-12 items-start pb-24 md:pb-32">
          <div className="md:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              Hello, I'm Tammy
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              One life. Many threads.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-lg leading-relaxed text-foreground/85">
              I'm a coach, an author, and a working real estate agent. The
              through-line in all of it is the same: helping people build
              something honest, steady, and theirs.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-foreground/85">
              Whether you found me through my book, my real estate work, or
              someone who said "you have to talk to Tammy" — welcome. Wander
              around. There's no pressure here.
            </p>
            <Link to="/about" data-testid="home-about-link">
              <Button variant="ghost" className="mt-8 rounded-full text-primary hover:bg-primary/10 px-0 hover:px-4 transition-all">
                More about me <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section className="bg-muted/40 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Where would you
                <br />
                <span className="italic text-primary">like to start?</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed self-end">
              Five doors into the same conversation. Pick the one that
              fits where you are right now.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-card border border-border rounded-2xl p-6 card-lift flex flex-col"
                data-testid={`pillar-card-${p.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <p.icon className="text-primary" size={26} strokeWidth={1.5} />
                <h3 className="font-serif text-xl mt-5 leading-tight">{p.title}</h3>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed flex-1">
                  {p.blurb}
                </p>
                <Link
                  to={p.to}
                  className="inline-flex items-center text-sm font-medium text-primary mt-6 hover:gap-2 gap-1 transition-all"
                  data-testid={`pillar-link-${p.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {p.cta} <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead magnet strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="relative bg-card border border-border rounded-3xl overflow-hidden p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center" data-testid="lead-magnet-strip">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-secondary/20 blur-2xl pointer-events-none" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Sparkles size={14} /> Free for new agents
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              The New Agent
              <br />
              Success Playbook.
            </h2>
            <p className="mt-6 text-foreground/75 leading-relaxed max-w-md">
              Tammy's playbook for new agents — getting started, buyer & seller
              systems, and the referrals and resources she's collected over 27
              years in the business.
            </p>
            <Button
              onClick={() => setMagnetOpen(true)}
              data-testid="lead-magnet-strip-btn"
              className="mt-8 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base"
            >
              Take me there <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
          <div className="relative">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl rotate-[-4deg]" />
              <img
                src="https://customer-assets.emergentagent.com/job_property-mentor-4/artifacts/lzc2u2uz_Screenshot%202026-06-03%20at%2011.33.21.png"
                alt="New Agent Success Playbook cover"
                className="relative rounded-2xl w-full shadow-xl rotate-[2deg]"
              />
            </div>
          </div>
        </div>
      </section>

      <LeadMagnetModal open={magnetOpen} onOpenChange={setMagnetOpen} />
    </div>
  );
}
